"use client"

import React, { useState, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, ArrowRight, AlertCircle, CheckCircle, KeyRound } from "lucide-react";
import { DomainContext } from "./LayoutCircuito";
import "../../styles/circuitoDesignSystem.css";

export default function ResetarSenha() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const domainCtx = useContext(DomainContext);
  const basePath = domainCtx?.basePath || "";
  const loginPath = domainCtx ? `${basePath}/inscrever-se/acesso` : "/login";
  
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  const handleResetar = async (e) => {
    e.preventDefault();
    
    if (senha !== confirmarSenha) {
      setErro("As senhas não coincidem.");
      return;
    }

    if (senha.length < 6) {
      setErro("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      setLoading(true);
      setErro("");
      
      const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api";
      await axios.post(`${API}/auth/resetar-senha`, { token, novaSenha: senha });
      
      setMensagem("Senha redefinida com sucesso! Redirecionando para o login...");
      
      setTimeout(() => {
          navigate(loginPath);
      }, 3000);

    } catch (error) {
      setErro(error.response?.data?.mensagem || "O link expirou ou é inválido. Solicite a recuperação novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="circuito-shell" style={{ minHeight: "80vh", display: "flex", alignItems: "center" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ maxWidth: 480, width: "100%", margin: "0 auto", padding: "40px 16px" }}
      >
        <div
          style={{
            background: "var(--c-card)",
            borderRadius: "var(--card-radius)",
            padding: "40px 32px",
            border: "var(--card-border)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Efeito de luz de fundo */}
          <div style={{
            position: "absolute",
            top: "-50px", left: "-50px", width: "150px", height: "150px",
            background: "var(--c-accent)",
            filter: "blur(100px)", opacity: 0.15, pointerEvents: "none"
          }} />

          <div style={{ textAlign: "center", marginBottom: "30px", position: "relative" }}>
            <motion.div 
              initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ delay: 0.2 }}
              style={{ display: "inline-flex", background: "rgba(255,255,255,0.05)", padding: 16, borderRadius: "50%", marginBottom: 16 }}
            >
              <KeyRound size={48} color="var(--c-accent)" strokeWidth={1.5} />
            </motion.div>

            {/* 💡 CORREÇÃO: color: "#ffffff" forçado no h2 */}
            <h2 style={{ color: "#ffffff", fontSize: "24px", fontWeight: 800, margin: "0 0 10px 0" }}>
              Criar Nova <span style={{ color: "var(--c-accent)" }}>Senha</span>
            </h2>
            
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.5", margin: 0 }}>
              Digite a sua nova senha abaixo para recuperar o acesso à sua conta.
            </p>
          </div>

          {erro && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)", color: "#f87171", padding: "14px 16px", borderRadius: "12px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px", fontSize: "14px" }}>
              <AlertCircle size={20} /> {erro}
            </motion.div>
          )}

          {mensagem ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.2)", color: "#10b981", padding: "20px", borderRadius: "12px", marginBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center", fontSize: "15px", lineHeight: "1.5" }}>
              <CheckCircle size={40} />
              {mensagem}
            </motion.div>
          ) : (
            <form onSubmit={handleResetar} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ position: "relative" }}>
                <Lock size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
                <input
                  type="password"
                  placeholder="Nova senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="c-input"
                  style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
                  required
                />
              </div>

              <div style={{ position: "relative" }}>
                <Lock size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
                <input
                  type="password"
                  placeholder="Confirme a nova senha"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="c-input"
                  style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
                  required
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="c-btn c-btn--primary"
                style={{ width: "100%", marginTop: "8px", height: "52px", fontSize: "16px", fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}
              >
                {loading ? "Salvando..." : <>Salvar Nova Senha <ArrowRight size={18} /></>}
              </motion.button>
            </form>
          )}

          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <Link to={loginPath} style={{ color: "rgba(255,255,255,0.6)", textDecoration: "underline", fontSize: "13px" }}>
              Lembrou a senha? Voltar para o Login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}